import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// ── Payment slips on the Quotation list (owner 2026-09-18) ──────────────────────────────────────
// A client who accepts a quotation on their no-login link can upload the transfer slip right there.
// Those slips already surface in the Bank Statement once a transaction matches — but the people who
// live in the Quotation list never saw them. A small badge on the row opens this viewer: full-size
// preview, download, and who confirmed it (confirming itself belongs in the Bank Statement, where
// the transaction it is being confirmed AGAINST actually exists).
//
// Deliberately not the Bank Statement's SlipCell: that component uploads and deletes, and neither
// belongs here. This is read-only.

const isPdf = (m: string, name = '') => /pdf/i.test(m || '') || /\.pdf$/i.test(name);
const fmtDT = (s: number) => (s ? new Date(s * 1000).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtKB = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export interface QuoSlip {
  id: string; filename: string; mime: string; size: number; created_at: number;
  confirmed_at?: number; confirmed_by_name?: string;
}

/** The row badge. Amber while anything is still TBC, green once every slip is confirmed. */
export function SlipBadge({ count, tbc, onClick }: { count: number; tbc: number; onClick: () => void }) {
  const { t } = useTranslation();
  if (!count) return null;
  const warn = tbc > 0;
  return (
    <span
      role="button"
      // The whole table row opens the quotation form — without this the badge would too.
      onClick={e => { e.stopPropagation(); onClick(); }}
      title={`${t('bs_slip_qo_badge_tip', { n: count })}${warn ? ` · ${t('bs_slip_qo_tbc', { n: tbc })}` : ''}`}
      style={{
        marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 4, verticalAlign: 'middle',
        fontSize: 11, fontWeight: 700, lineHeight: 1.6, padding: '1px 7px', borderRadius: 999, cursor: 'pointer',
        color: warn ? '#8A6212' : '#0F6B49', background: warn ? '#FEF6E0' : '#E6F6EF',
        border: `1px solid ${warn ? '#F0DCA6' : '#B9E5D2'}`,
      }}>
      <i className="ti ti-receipt-2" style={{ fontSize: 11 }} aria-hidden />
      {warn ? `${count} · TBC` : count}
    </span>
  );
}

function SlipPreview({ quotationId, slip }: { quotationId: string; slip: QuoSlip }) {
  const { t } = useTranslation();
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);
  const kept = useRef('');
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const url = await api.quotationPaySlipObjectUrl(quotationId, slip.id);
        if (!live) { URL.revokeObjectURL(url); return; }
        kept.current = url; setSrc(url);
      } catch { if (live) setFailed(true); }
    })();
    return () => { live = false; if (kept.current) { const u = kept.current; kept.current = ''; setTimeout(() => URL.revokeObjectURL(u), 1500); } };
  }, [quotationId, slip.id]);

  const ok = !!Number(slip.confirmed_at);
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 12px', borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, wordBreak: 'break-all', flex: 1, minWidth: 140 }}>{slip.filename}</span>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{fmtDT(slip.created_at)} · {fmtKB(slip.size)}</span>
        <span style={{
          fontSize: 10.5, fontWeight: 800, padding: '1px 8px', borderRadius: 999, whiteSpace: 'nowrap',
          color: ok ? '#0F6B49' : '#8A6212', background: ok ? '#E6F6EF' : '#FEF6E0', border: `1px solid ${ok ? '#B9E5D2' : '#F0DCA6'}`,
        }}>{ok ? t('bs_slip_confirmed_by', { who: slip.confirmed_by_name || '—' }) : 'TBC'}</span>
        {src && <a href={src} download={slip.filename} onClick={e => e.stopPropagation()} title={slip.filename}
          style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--accent)', textDecoration: 'none', whiteSpace: 'nowrap' }}>
          <i className="ti ti-download" style={{ fontSize: 12, verticalAlign: '-2px', marginRight: 3 }} />{slip.filename.split('.').pop()?.toUpperCase() || 'FILE'}
        </a>}
      </div>
      <div style={{ padding: 10, display: 'flex', justifyContent: 'center', background: 'var(--bg-subtle, #FAFAFA)' }}>
        {failed ? <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}><i className="ti ti-file-alert" style={{ marginRight: 5 }} />{t('bs_slip_preview_fail')}</span>
          : !src ? <i className="ti ti-loader" style={{ fontSize: 20, color: 'var(--text-tertiary)', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
          : isPdf(slip.mime, slip.filename)
            ? <iframe src={src} title={slip.filename} style={{ width: '100%', height: 520, border: 'none', background: '#fff' }} />
            : <img src={src} alt={slip.filename} style={{ maxWidth: '100%', maxHeight: 620, borderRadius: 6 }} />}
      </div>
    </div>
  );
}

export default function QuoSlipViewer({ quotationId, docNo, clientName, onClose }: {
  quotationId: string; docNo?: string; clientName?: string; onClose: () => void;
}) {
  const { t } = useTranslation();
  const [slips, setSlips] = useState<QuoSlip[]>([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try { const r: any = await api.quotationPaySlips(quotationId); if (live) setSlips(r.slips || []); }
      catch (e: any) { if (live) setErr(e.message); }
      finally { if (live) setBusy(false); }
    })();
    return () => { live = false; };
  }, [quotationId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 11500, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px 24px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(760px, 100%)', background: 'var(--bg-surface)', borderRadius: 14, boxShadow: '0 20px 70px #0006', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{t('bs_slip_qo_title')}</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{docNo || ''}{clientName ? ` · ${clientName}` : ''}</div>
          </div>
          <button onClick={onClose} title={t('close')} style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <i className="ti ti-x" />
          </button>
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '76vh', overflowY: 'auto' }}>
          {busy && <i className="ti ti-loader" style={{ fontSize: 22, color: 'var(--text-tertiary)', alignSelf: 'center', display: 'inline-block', animation: 'spin 1s linear infinite' }} />}
          {!!err && <div style={{ fontSize: 12.5, color: '#B42318' }}>{err}</div>}
          {!busy && !err && !slips.length && <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{t('bs_slip_qo_none')}</div>}
          {slips.map(s => <SlipPreview key={s.id} quotationId={quotationId} slip={s} />)}
        </div>
      </div>
    </div>
  );
}
